import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, isBackendConfigured } from '../utils/backendApi';
import { useActions, when } from '../components/shipRail';

// /admin/helpdesk — the tech help desk's own page: what waits for a
// decision (the same approve / decline the digest mails carry), what
// autopilot did on its own, and the autonomy the desk runs at. Server side:
// GET /desk/approvals, POST /desk/approvals/:id/:action, GET /desk/log,
// POST /desk/digest/send; the autonomy is config/app desk.autonomy.
const KIND = { merge: 'Merge', reply: 'Reply', 'feature-triage': 'Feature', promote: 'Promote' };
const FILTERS = [['pending', 'Waiting'], ['done', 'Approved'], ['declined', 'Declined'], ['failed', 'Failed'], ['all', 'All']];

const openUrl = (a) => (a.col === 'bugReports' ? `/admin/bugs/${a.docId}` : a.col === 'featureRequests' ? `/admin/features/${a.docId}` : `/admin/support?ticket=${a.docId}`);

function Item({ a, busy, onAct }) {
  const guards = [...new Set((a.guards || []).map((g) => g.guard))];
  const canApprove = !(a.kind === 'reply' && !a.draft);
  return (
    <li className="cs-support-row" style={{ display: 'block' }}>
      <div className="bug-row-top">
        <strong>{a.title || a.docId}</strong>
        <span className={`mf-chip ${a.status === 'pending' ? 'is-info' : a.status === 'done' ? 'is-ok' : a.status === 'failed' ? 'is-danger' : ''}`}>{KIND[a.kind] || a.kind} · {a.status}</span>
      </div>
      <span className="cs-help">
        {a.repo ? `${a.repo} repo · ` : ''}{a.orgName ? `${a.orgName} · ` : ''}{a.reporter ? `${a.reporter} · ` : ''}{when(a.createdAt)}
        {a.files?.length ? ` · ${a.files.length} file${a.files.length === 1 ? '' : 's'}` : ''}{guards.length ? ` · guards: ${guards.join(', ')}` : ''}
      </span>
      <p className="cs-help" style={{ marginTop: 6 }}>{a.reason}</p>
      {a.summary ? <p className="bug-detail-desc" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{String(a.summary).slice(0, 700)}</p> : null}
      {a.draft ? <p className="bug-detail-desc" style={{ whiteSpace: 'pre-wrap', fontSize: 13, borderLeft: '3px solid var(--cs-line, #2a3340)', paddingLeft: 10 }}>{String(a.draft).slice(0, 900)}</p> : null}
      {a.error ? <p className="cs-land-micro" role="alert">{a.error}</p> : null}
      {a.decidedBy ? <p className="cs-help">{a.status} by {a.decidedBy} · {when(a.decidedAt)}</p> : null}
      <div className="bug-step-actions" data-agent-guard>
        {a.status === 'pending' && canApprove && <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => onAct(a.id, 'approve')} disabled={!!busy}>{busy === a.id ? 'Working…' : 'Approve'}</button>}
        {a.status === 'pending' && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onAct(a.id, 'decline')} disabled={!!busy}>{canApprove ? 'Decline' : 'Send to me'}</button>}
        {a.prUrl && <a className="cs-btn cs-btn-outline cs-btn-sm" href={a.prUrl} target="_blank" rel="noreferrer">PR ↗</a>}
        <Link to={openUrl(a)} className="cs-help">Open →</Link>
      </div>
    </li>
  );
}

export default function AdminHelpdesk() {
  const [filter, setFilter] = useState('pending');
  const [data, setData] = useState(null);
  const [log, setLog] = useState(null);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    if (!isBackendConfigured()) { setLoadError('The API is not configured.'); return; }
    try {
      const [d, l] = await Promise.all([apiGet(`/desk/approvals?status=${filter}`), apiGet('/desk/log?days=7')]);
      setData(d);
      setLog(l.log || []);
      setLoadError('');
    } catch (err) {
      setLoadError(err.message);
    }
  }, [filter]);
  const { busy, error, notice, act } = useActions(load);
  useEffect(() => { load(); }, [load]);

  const onAct = (id, action) => act(id, async () => { const r = await apiPost(`/desk/approvals/${id}/${action}`, {}); return { message: r.message || `${action}d.` }; });
  const sendDigest = () => act('digest', async () => { const r = await apiPost('/desk/digest/send', {}); return { message: r.skipped ? `Nothing to send (${r.skipped}).` : `Digest sent to ${r.to} with ${r.items} item(s).` }; });

  const approvals = data?.approvals || [];
  const cfg = data?.config || {};
  const auto = log ? log.filter((e) => e.by === 'autopilot') : [];

  return (
    <div className="cs-store" data-nova-note="Tech help desk: approvals waiting for the founder (same approve/decline as the digest mail), the autopilot log, and the autonomy level from config/app desk.autonomy.">
      <header className="cs-store-hero">
        <h1>Help desk</h1>
        <p>
          Bug reports, feature requests and support tickets from every org are triaged, answered and fixed by the desk on its own at autonomy <strong>{data?.autonomy || '…'}</strong>
          {data && !data.enabled ? ' (desk off)' : ''}. Guarded changes ({(cfg.guarded || []).join(', ') || '…'}), prod promotes, money topics and verdicts without a code change wait here —
          and reach you by mail at most {cfg.approvals?.maxPerDay ?? 3} times a day with one-click links. Change the level in <Link to="/admin/apps">config/app</Link> → <span className="aa-mono">desk.autonomy</span>.
        </p>
      </header>
      <section className="cs-store-section cs-support-desk">
        <div className="cs-sw-card">
          <div className="bug-list-head">
            <h3>Decisions</h3>
            <div className="bug-step-actions" data-agent-guard>
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={sendDigest} disabled={!!busy}>{busy === 'digest' ? 'Sending…' : 'Mail me the digest now'}</button>
            </div>
          </div>
          <div className="bug-queue-filters" role="tablist" aria-label="Filter">
            {FILTERS.map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={filter === id} className={filter === id ? 'is-on' : ''} onClick={() => setFilter(id)}>{label}</button>
            ))}
          </div>
          {(error || loadError) && <p className="cs-land-micro" role="alert">{error || loadError}</p>}
          {notice && <p className="cs-help" role="status">{notice}</p>}
          {!data && !loadError && <p className="cs-help">Loading…</p>}
          {data && approvals.length === 0 && <p className="cs-help">Nothing here.</p>}
          <ul className="cs-support-list">
            {approvals.map((a) => <Item key={a.id} a={a} busy={busy} onAct={onAct} />)}
          </ul>
        </div>
        <div className="cs-sw-card" style={{ marginTop: 16 }}>
          <div className="bug-list-head">
            <h3>What autopilot did · 7 days</h3>
            <span className="cs-help">{auto.length} action{auto.length === 1 ? '' : 's'}</span>
          </div>
          {log && auto.length === 0 && <p className="cs-help">Nothing yet.</p>}
          <ul className="cs-support-list">
            {auto.slice(0, 100).map((e) => (
              <li key={e.id} className="cs-support-row" style={{ display: 'block' }}>
                <div className="bug-row-top"><strong>{e.action}</strong><span className="cs-help">{when(e.at)}</span></div>
                <span className="cs-help">{e.orgName ? `${e.orgName} · ` : ''}{e.detail}{e.col && e.docId ? <> · <Link to={openUrl(e)}>open</Link></> : null}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
